import { AgGridAngular, AgGridModule } from "@ag-grid-community/angular";
import {
  ColDef,
  GridOptions,
  ICellRendererParams,
  RowDragEvent,
  ValueFormatterParams,
  ValueGetterParams,
} from "@ag-grid-community/core";
import { CommonModule } from "@angular/common";
import { Component, DestroyRef, inject, input, OnInit, signal, ViewChild } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Router } from "@angular/router";
import {
  AccessLevel,
  AccountMarketplace,
  AlgoMode,
  Currency,
  MatchType,
  StrategyAsin,
  StrategyKeywords,
  StrategyStateEnum,
  StrategyTactic,
  StrategyType,
  TacticType,
  Targeting,
  UpdateStrategyTopOfSearchRankingsActionEnum,
  User,
} from "@front/m19-api-client";
import { gridConfig } from "@front/m19-grid-config";
import {
  AlgoModeStr,
  getStrategies,
  Marketplaces,
  StrategyEx,
  StrategyGroupEx,
  StrategyTargetingType,
} from "@front/m19-models";
import { AccountSelectionService, Constant, SpStrategiesService, StrategyService } from "@front/m19-services";
import {
  ConfirmPopupComponent,
  IAccordionComponent,
  IAlertComponent,
  IBadgeComponent,
  IButtonComponent,
  IToggleComponent,
  ModalOptions,
  ModalService,
  ToasterService,
} from "@front/m19-ui";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { actionColumnProperties } from "@m19-board/grid-config/grid-columns";
import {
  ActionButton,
  ActionButtonsComponent,
} from "@m19-board/insights/advertising-stats/action-buttons/action-buttons.component";
import { AlgoTargetRendererComponent } from "@m19-board/insights/advertising-stats/algo-target-renderer/algo-target-renderer.component";
import {
  SwitchTargetAlgoModalComponent,
  TargetAlgoModeSelectionData,
} from "@m19-board/insights/advertising-stats/switch-target-algo-modal/switch-target-algo-modal.component";
import { InputModalComponent, InputModalData } from "@m19-board/shared/input-modal/input-modal.component";
import { StateBadgeComponent } from "@m19-board/shared/state-badge/state-badge.component";
import {
  StrategyActivityModalComponent,
  StrategyActivityModalData,
} from "@m19-board/strategies/strategies/strategy-activities/strategy-activities-modal.component";
import { StrategyStatsComponent } from "@m19-board/strategies/strategies/strategy-stats/stategy-stats.component";
import { SwitchAlgoModeComponent } from "@m19-board/strategies/strategy-page/switch-algo-mode-popup/switch-algo-mode.component";
import { keywordRankingAvailableFor } from "@m19-board/tracking/KeywordRankingAvailability";
import { ICON_CHART_LINE, ICON_COPY_O, ICON_LIST, ICON_TRASH_O } from "@m19-board/utils/iconsLabels";
import { forkJoin, map, switchMap, take } from "rxjs";
import {
  AdvancedSettingsModalComponent,
  AdvancedSettingsModalData,
} from "../../advanced-settings-modal/advanced-settings-modal.component";
import {
  BlacklistModalComponent,
  BlacklistModalData,
  UpdateOperations,
} from "../../blacklist/blacklist-modal.component";
import { BlacklistViewComponent } from "../../blacklist/blacklist-view.component";
import { TargetingsModalComponent, TargetingsModalData } from "../../targetings-modal/targetings-modal.component";
import {
  StrategyGroupProductsModalData,
  StrategyProductsModalComponent,
} from "../strategy-products-modal/strategy-products-modal.component";
import {
  StrategyTacticModalComponent,
  StrategyTacticModalData,
} from "../strategy-tactic-modal/strategy-tactic-modal.component";
@Component({
  selector: "strategy-group-strategies",
  standalone: true,
  imports: [
    CommonModule,
    TranslocoDirective,
    BlacklistViewComponent,
    IBadgeComponent,
    AgGridModule,
    IButtonComponent,
    IAccordionComponent,
    IAlertComponent,
  ],
  templateUrl: "./strategy-group-strategies.component.html",
})
export class StrategyGroupStrategiesComponent {
  private readonly translocoService = inject(TranslocoService);
  private readonly strategyService = inject(StrategyService);
  private readonly toastrService = inject(ToasterService);
  private readonly modalService = inject(ModalService);
  private readonly spStrategiesService = inject(SpStrategiesService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  readonly MAX_BLACKLIST_ITEMS = Constant.maxKwTargetingByStrategy;

  StrategyType = StrategyType;
  private readonly EDIT_ICON_PROPS = {
    icon: "icon-[lucide--edit-3]",
    size: "sm",
    color: "gray",
    variant: "ghost",
    trailing: true,
    iconOnHover: true,
  };

  private readonly defaultColDef: ColDef<StrategyEx> = {
    flex: 1,
    resizable: true,
    cellClass: "flex items-center",
  };

  private readonly defaultGridOptions: GridOptions<StrategyEx> = {
    sideBar: false,
    defaultColDef: this.defaultColDef,
    enableRangeSelection: false,
    suppressRowHoverHighlight: true,
    animateRows: true,
  };

  strategyGroup = input<StrategyGroupEx>();
  isReadOnly = input<boolean>(false);
  accountMarketplace = input<AccountMarketplace>();
  user = input<User>();

  isBlacklistOpen = signal(false);

  readonly StrategyColumnDefinitions: Record<string, ColDef<StrategyEx>> = {
    state: {
      field: "state",
      headerName: this.translocoService.translate("billing-customer.state", {}, "en"),
      headerValueGetter: (_) => this.translocoService.translate("billing-customer.state"),
      cellRendererSelector: (params) => {
        if (!params.value) return undefined;
        return {
          component: StateBadgeComponent,
          params: {
            state: params.value,
            disabled: this.isReadOnly(),
            onStateChange: () => {
              const newState =
                params.value === StrategyStateEnum.ENABLED ? StrategyStateEnum.PAUSED : StrategyStateEnum.ENABLED;
              this.updateStrategyState(params.data!.strategyId, newState);
            },
          },
        };
      },
      minWidth: 180,
    },
    strategyName: {
      headerName: this.translocoService.translate("sd-strategy-creation.strategy_name", {}, "en"),
      headerValueGetter: () => this.translocoService.translate("sd-strategy-creation.strategy_name"),
      field: "name",
      cellRendererSelector: (params) => {
        return {
          component: IButtonComponent,
          params: {
            ...this.EDIT_ICON_PROPS,
            label: params.value,
            tooltipValue: this.translocoService.translate("sp-strategy-group-page.edit_strategy_name"),
            disabled: this.isReadOnly(),
            clickAction: () => this.openUpdateStrategyNameModal(params.data!.strategyId, params.value),
          },
        };
      },
    },
    algo: {
      headerName: this.translocoService.translate("sd-strategy-creation.algorithm", {}, "en"),
      headerValueGetter: () => this.translocoService.translate("sd-strategy-creation.algorithm"),
      field: "algorithm",
      cellRenderer: IButtonComponent,
      cellRendererParams: (params: ICellRendererParams) => ({
        ...this.EDIT_ICON_PROPS,
        tooltipValue: this.translocoService.translate("sp-strategy-group-page.change_algorithm"),
        label: this.translocoService.translate(AlgoModeStr[params.value as AlgoMode]?.shortDescription),
        clickAction: () => {
          if (this.isReadOnly()) return;
          if (params.data.primeDayBoost) {
            // cannot change algo when prime day boost is active
            this.toastrService.warning(
              this.translocoService.translate("sp-strategy-group-page.cannot_change_algorithm_while_promo_day_boost"),
            );
            return;
          }
          this.modalService.openModal(SwitchAlgoModeComponent, {
            modalTitle: "Switch Algo Mode",
            data: {
              strategy: params.data,
            },
          });
        },
      }),
    },
    target: {
      headerName: this.translocoService.translate("strategy-table.target", {}, "en"),
      headerValueGetter: () => this.translocoService.translate("strategy-table.target"),
      colId: "target",
      cellRenderer: AlgoTargetRendererComponent,
      cellRendererParams: (params: ICellRendererParams<StrategyEx>) => {
        return {
          strategy: params.data,
          locale: this.user()!.locale,
          currency: this.accountMarketplace()!.marketplace
            ? Marketplaces[this.accountMarketplace()!.marketplace].currency
            : Currency.USD,
          readonly: this.isReadOnly(),
          organizationId: this.accountMarketplace()!.resourceOrganizationId!,
        };
      },
    },
    products: {
      headerName: this.translocoService.translate("sd-strategy-creation.advertised_products", {}, "en"),
      headerValueGetter: () => this.translocoService.translate("sd-strategy-creation.advertised_products"),
      field: "asins",
      valueFormatter: (params: ValueFormatterParams<StrategyEx, StrategyAsin[]>) =>
        !params.value?.length ? "Add products" : params.value!.length.toString(),
      cellRenderer: IButtonComponent,
      cellRendererParams: (params: ICellRendererParams<any>) => ({
        ...this.EDIT_ICON_PROPS,
        tooltipValue: "View advertised products",
        label: params.value.length,
        clickAction: () => {
          this.displayStrategyProductsModal(params.data!);
        },
      }),
    },
    targetings: {
      headerName: this.translocoService.translate("sd-strategy-creation.targetings", {}, "en"),
      headerValueGetter: () => this.translocoService.translate("sd-strategy-creation.targetings"),
      field: "targetings",
      cellRenderer: IButtonComponent,
      cellRendererParams: (params: ICellRendererParams<StrategyEx, Targeting[]>) => {
        return {
          ...this.EDIT_ICON_PROPS,
          tooltipValue: this.translocoService.translate("sp-strategy-group-page.view_targetings"),
          label: this.formatTargetings(params.value),
          clickAction: () => {
            this.openTargetingsModal(params.data!);
          },
        };
      },
    },
    tosro: {
      headerName: this.translocoService.translate("sp-strategy-group-page.tosro", {}, "en"),
      headerValueGetter: () => this.translocoService.translate("sp-strategy-group-page.tosro"),
      colId: "TOSRO",
      headerTooltip: "Top of Search Rankings Optimizer",
      suppressSizeToFit: true,
      valueGetter: (params) => {
        return params.data?.topOfSearchRankings;
      },
      cellRenderer: IToggleComponent,
      cellRendererParams: (params: ICellRendererParams<StrategyEx, StrategyKeywords[] | undefined>) => {
        const supportedMarketplace = keywordRankingAvailableFor(params.data!.marketplace);
        const hasOneExactTargeting = params.data!.targetings.findIndex((t) => t.matchType == MatchType.exact) >= 0;
        const isDisabled = !supportedMarketplace || this.isReadOnly() || !hasOneExactTargeting;
        const isChecked = !isDisabled && params.value!.length > 0;

        return {
          checked: isChecked,
          disabled: isDisabled,
          tooltipValue: this.getTosroTooltip(supportedMarketplace, hasOneExactTargeting),
          checkedChangeAction: () => {
            this.updateStrategyOptimizer(params.data!, !isChecked);
          },
        };
      },
    },
  };

  readonly ActionButtons: Record<string, (strategy: StrategyEx) => ActionButton> = {
    stats: (strategy) => ({
      icon: ICON_CHART_LINE,
      tooltip: this.translocoService.translate("sp-strategy-group-page.display_strategy_stats"),
      onClick: () => {
        this.openStrategyStats(strategy);
      },
    }),
    activities: (strategy) => ({
      icon: ICON_LIST,
      tooltip: this.translocoService.translate("sp-strategy-group-page.display_strategy_activities"),
      onClick: () => {
        this.openStrategyActivities(strategy);
      },
    }),
    advanced: (strategy) => {
      const subItems: ActionButton[] = [
        {
          title: this.translocoService.translate("sp-strategy-group-page.open_advanced_settings"),
          onClick: () => {
            this.modalService.openModal<AdvancedSettingsModalData, void>(AdvancedSettingsModalComponent, {
              modalTitle: this.translocoService.translate("sp-strategy-group-page.advanced_settings"),
              data: {
                accountMarketplace: this.accountMarketplace()!,
                strategy: strategy,
                isReadOnly: this.isReadOnly(),
                locale: this.user()!.locale,
                currencyCode: this.accountMarketplace()!.marketplace
                  ? Marketplaces[this.accountMarketplace()!.marketplace].currency
                  : Currency.USD,
              },
            });
          },
        },
      ];

      if (strategy.algoMode == AlgoMode.ACOS_TARGET) {
        subItems.push({
          title: this.translocoService.translate("sp-strategy-group-page.average_daily_budget"),
          disabled: this.isReadOnly() || !!strategy.primeDayBoost,
          onClick: () => {
            this.openAverageDailyBudgetModal(strategy);
          },
        });
      }

      return {
        icon: "icon-[mdi--ellipsis-horizontal]",
        tooltip: this.translocoService.translate("sp-strategy-group-page.advanced_settings"),
        subItems,
      };
    },
    copyStrategyId: (strategy) => ({
      icon: ICON_COPY_O,
      tooltip: this.translocoService.translate("sp-strategy-group-page.copy_strategy_id"),
      onClick: () => {
        navigator.clipboard.writeText(strategy.strategyId.toString());
        this.toastrService.success(
          this.translocoService.translate("sp-strategy-group-page.strategy_id_copied_to_clipboard"),
          this.translocoService.translate("sp-strategy-group-page.strategy_id_copied"),
        );
      },
    }),
    delete: (strategy) => ({
      icon: ICON_TRASH_O,
      tooltip: this.translocoService.translate("sp-strategy-group-page.remove_strategy", [strategy.strategyName]),
      disabled: this.isReadOnly(),
      color: "red",
      onClick: () => {
        const modalRef = this.modalService.openModal(ConfirmPopupComponent, {
          modalTitle: this.translocoService.translate("activity-service.strategy_deletion"),
          data: {
            message: this.translocoService.translate(
              "sp-strategy-group-page.are_you_sure_you_want_to_delete_this_strategy",
            ),
          },
        });
        modalRef
          .afterClosed()
          .pipe(
            switchMap(() =>
              this.strategyService.deleteStrategy(strategy, this.accountMarketplace()!.resourceOrganizationId!),
            ),
          )
          .subscribe({
            next: () => {
              this.toastrService.success(
                this.translocoService.translate("sp-strategy-group-page.strategy_successfully_deleted", [
                  strategy.name,
                ]),
                this.translocoService.translate("sp-strategy-group-page.strategy_deleted"),
              );
            },
            error: (err) => {
              this.toastrService.error(
                this.translocoService.translate("sp-strategy-group-page.error_deleting", [strategy.name, err]),
                this.translocoService.translate("sp-strategy-group-page.strategy_deletion_error"),
              );
            },
          });
      },
    }),
    deleteProductStrategy: (strategy) => ({
      icon: ICON_TRASH_O,
      tooltip: this.translocoService.translate("sp-strategy-group-page.remove_strategy", [strategy.strategyName]),
      disabled: this.isReadOnly(),
      color: "red",
      onClick: () => {
        const impactedStrats = new Map<StrategyEx, string[]>();
        for (const strategyAsin of strategy.asins) {
          for (const strat of this.strategyService.getStrategyPerAsin(this.strategyGroup()!).get(strategyAsin.asin) ??
            []) {
            if (strat.strategyId != strategy.strategyId) {
              if (impactedStrats.has(strat)) {
                impactedStrats.get(strat)!.push(strategyAsin.asin);
              } else {
                impactedStrats.set(strat, [strategyAsin.asin]);
              }
            }
          }
        }
        if (impactedStrats.size == 0) {
          //  confirmation modal
          const modalRef = this.modalService.openModal(ConfirmPopupComponent, {
            modalTitle: this.translocoService.translate("activity-service.strategy_deletion"),
            data: {
              message: this.translocoService.translate(
                "sp-strategy-group-page.are_you_sure_you_want_to_delete_this_strategy",
              ),
            },
          });
          modalRef
            .afterClosed()
            .pipe(
              switchMap(() =>
                this.strategyService.deleteStrategy(strategy, this.accountMarketplace()!.resourceOrganizationId!),
              ),
            )
            .subscribe({
              next: () => {
                this.toastrService.success(
                  this.translocoService.translate("sp-strategy-group-page.strategy_successfully_deleted", [
                    strategy.name,
                  ]),
                  this.translocoService.translate("sp-strategy-group-page.strategy_deleted"),
                );
              },
              error: (err) => {
                this.toastrService.error(
                  this.translocoService.translate("sp-strategy-group-page.error_deleting", [strategy.name, err]),
                  this.translocoService.translate("sp-strategy-group-page.strategy_deletion_error"),
                );
              },
            });
          return;
        }
        //  confirmation modal
        const modalRef = this.modalService.openModal(ConfirmPopupComponent, {
          modalTitle: this.translocoService.translate("sp-strategy-group-page.main_strategy_deletion"),
          data: {
            message: `Products of this strategy are used in ${impactedStrats.size} brand defense or focus ${
              impactedStrats.size > 1 ? "strategies" : "strategy"
            } (${Array.from(impactedStrats.keys())
              .map((s) => `"${s.name}"`)
              .join(", ")}). They will stop running for these products.`,
          },
        });
        modalRef
          .afterClosed()
          .pipe(
            switchMap(() =>
              forkJoin(
                Array.from(impactedStrats.entries()).map(([strat, asins]) =>
                  this.strategyService.deleteAsinsFromStrategy(strat, asins),
                ),
              ),
            ),
            // TODO: also stop strategies if no more ASINs
            switchMap(() =>
              this.strategyService.deleteStrategy(strategy, this.accountMarketplace()!.resourceOrganizationId!),
            ),
          )
          .subscribe({
            next: () => {
              this.toastrService.success(
                this.translocoService.translate("sp-strategy-group-page.main_strategy_successfully_deleted", [
                  strategy.name,
                ]),
                this.translocoService.translate("sp-strategy-group-page.strategy_deleted"),
              );
            },
            error: (err) => {
              this.toastrService.error(
                this.translocoService.translate("sp-strategy-group-page.error_deleting_main_strategy", [
                  strategy.name,
                  err,
                ]),
                this.translocoService.translate("sp-strategy-group-page.strategy_deletion_error"),
              );
            },
          });
      },
    }),
  };

  rowDragHandler = (strategyType: StrategyType) => (event: RowDragEvent<StrategyEx, any>) => {
    const strategy = event.node.data;
    const index = event.overIndex;
    if (event.overNode?.rowIndex == index) {
      return; // no move
    }
    let priority = 0;
    const strategies = getStrategies(this.strategyGroup()!, strategyType);
    if (index == 0) {
      priority = Math.floor((Number.MIN_SAFE_INTEGER + strategies[0].priority!) / 2);
    } else if (index == strategies.length - 1) {
      priority = Math.floor((Number.MAX_SAFE_INTEGER + strategies[index].priority!) / 2);
    } else {
      priority = Math.floor((strategies[index - 1].priority! + strategies[index].priority!) / 2);
    }
    this.strategyService
      .updateStrategyPriority(
        this.accountMarketplace()!.accountId,
        this.accountMarketplace()!.marketplace,
        this.accountMarketplace()!.resourceOrganizationId!,
        strategy!.strategyId,
        priority,
      )
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe({
        next: () => {
          this.toastrService.success(this.translocoService.translate("sp-strategy-group-page.priority_updated"));
        },
        error: (err: string) => {
          this.toastrService.error(
            err,
            this.translocoService.translate("sp-strategy-group-page.strategy_priority_update_error"),
          );
        },
      });
  };

  // Brand Defense Strategies
  @ViewChild("brandStrategies", { static: false }) brandStrategiesGrid!: AgGridAngular;
  readonly brandStrategyGridOptions: GridOptions<StrategyEx> = {
    ...gridConfig({ gridKey: "brandStrategies", autoFitDefault: true }),
    ...this.defaultGridOptions,
    rowDragText: () =>
      this.translocoService.translate("sp-strategy-group-page.drag_strategy_up_or_down_to_modify_its_priority"),
    onRowDragEnd: this.rowDragHandler(StrategyType.BRAND),
    columnDefs: [
      { ...this.StrategyColumnDefinitions["state"]!, rowDrag: true },
      this.StrategyColumnDefinitions["strategyName"]!,
      this.StrategyColumnDefinitions["algo"]!,
      this.StrategyColumnDefinitions["target"]!,
      this.StrategyColumnDefinitions["products"]!,
      this.StrategyColumnDefinitions["targetings"]!,
      this.StrategyColumnDefinitions["tosro"]!,
      this.actionButtonColumn(),
    ],
  };

  // Focus Strategies
  @ViewChild("keywordStrategies", { static: false }) keywordStrategiesGrid!: AgGridAngular;
  readonly keywordStrategyGridOptions: GridOptions<StrategyEx> = {
    ...gridConfig({ gridKey: "keywordStrategies", autoFitDefault: true }),
    ...this.defaultGridOptions,
    rowDragText: () =>
      this.translocoService.translate("sp-strategy-group-page.drag_strategy_up_or_down_to_modify_its_priority"),
    onRowDragEnd: this.rowDragHandler(StrategyType.KEYWORD),
    columnDefs: [
      { ...this.StrategyColumnDefinitions["state"]!, rowDrag: true },
      this.StrategyColumnDefinitions["strategyName"]!,
      this.StrategyColumnDefinitions["algo"]!,
      this.StrategyColumnDefinitions["target"]!,
      this.StrategyColumnDefinitions["products"]!,
      this.StrategyColumnDefinitions["targetings"]!,
      this.StrategyColumnDefinitions["tosro"]!,
      this.actionButtonColumn(),
    ],
  };

  // Main Strategies
  @ViewChild("productStrategies", { static: false }) productStrategiesGrid!: AgGridAngular;
  readonly productStrategyGridOptions: GridOptions<StrategyEx> = {
    ...gridConfig({ gridKey: "productStrategies", autoFitDefault: true }),
    ...this.defaultGridOptions,
    columnDefs: [
      this.StrategyColumnDefinitions["state"]!,
      this.StrategyColumnDefinitions["strategyName"]!,
      this.StrategyColumnDefinitions["algo"]!,
      this.StrategyColumnDefinitions["target"]!,
      this.StrategyColumnDefinitions["products"]!,
      this.StrategyColumnDefinitions["tosro"]!,
      {
        headerName: this.translocoService.translate("strategy-page.tactics", {}, "en"),
        headerValueGetter: () => this.translocoService.translate("strategy-page.tactics"),
        valueGetter: (params: ValueGetterParams<StrategyEx>) =>
          params.data?.tactics.filter((t) => t.tacticType == TacticType.LEGACY),
        valueFormatter: (params: ValueFormatterParams<StrategyEx, Targeting[]>) =>
          !params.value || params.value.length == 0 ? "No Tactic" : params.value.length.toString(),
        cellRenderer: IButtonComponent,
        cellRendererParams: (params: ICellRendererParams<StrategyEx, StrategyTactic[]>) => ({
          ...this.EDIT_ICON_PROPS,
          tooltipValue: "View Tactics",
          label: params.value?.length == 0 ? "No Tactic" : params.value!.length.toString(),
          clickAction: () => {
            this.openTacticsModal(params.data!, params.value!, TacticType.LEGACY);
          },
        }),
      },
      {
        headerName: this.translocoService.translate("common.blacklist", {}, "en"),
        headerValueGetter: () => this.translocoService.translate("common.blacklist"),
        valueGetter: (params: ValueGetterParams<StrategyEx>) =>
          params.data!.tactics.filter((t) => t.tacticType == TacticType.BLACKLIST),
        valueFormatter: (params: ValueFormatterParams<StrategyEx, Targeting[]>) =>
          params.value!.length == 0 ? "" : params.value!.length.toString(),
        cellRenderer: IButtonComponent,
        cellRendererParams: (params: ICellRendererParams) => ({
          ...this.EDIT_ICON_PROPS,
          tooltipValue: "View Blacklist",
          label: params.value.length == 0 ? "0" : params.value.length.toString(),
          clickAction: () => {
            this.openTacticsModal(params.data!, params.value!, TacticType.BLACKLIST);
          },
        }),
      },
      this.actionButtonColumn(),
    ],
  };

  openAverageDailyBudgetModal(strategy: StrategyEx) {
    this.modalService.openModal<TargetAlgoModeSelectionData, void>(SwitchTargetAlgoModalComponent, {
      modalTitle: this.translocoService.translate("sp-strategy-group-page.daily_budget"),
      data: {
        strategy,
        readonly: this.isReadOnly(),
        organizationId: this.accountMarketplace()!.resourceOrganizationId!,
      },
    });
  }

  openSubStrategyCreationPage(strategyType: StrategyType) {
    this.router.navigate(
      [`/advertising/sponsored-product/strategy-group/${this.strategyGroup()!.strategyGroupId}/create-strategy`],
      {
        queryParamsHandling: "merge",
        queryParams: {
          strategyType,
        },
      },
    );
  }

  private displayStrategyProductsModal(strategy: StrategyEx) {
    this.modalService.openModal<StrategyGroupProductsModalData, void>(StrategyProductsModalComponent, {
      modalTitle: this.translocoService.translate("sd-strategy-creation.advertised_products"),
      data: { strategy, isReadOnly: this.isReadOnly(), strategyGroup: this.strategyGroup()! },
    });
  }

  private openTacticsModal(strategy: StrategyEx, tactics: StrategyTactic[], tacticType: TacticType) {
    this.modalService.openModal<StrategyTacticModalData, void>(StrategyTacticModalComponent, {
      modalTitle: this.translocoService.translate("strategy-page.tactics"),
      data: { strategy, isReadOnly: this.isReadOnly(), strategyGroup: this.strategyGroup()!, tactics, tacticType },
    });
  }

  private openUpdateStrategyNameModal(strategyId: number, currentName: string) {
    const ref = this.modalService.openModal<InputModalData, string>(InputModalComponent, {
      modalTitle: this.translocoService.translate("strategy-page.update_strategy_name"),
      data: {
        initialValue: currentName,
        maxLength: 80,
      },
    });
    ref
      .afterClosed()
      .pipe(
        switchMap((newName) => {
          return this.updateStrategyName(strategyId, newName);
        }),
      )
      .subscribe({
        next: () => {
          this.toastrService.success(
            this.translocoService.translate("sp-strategy-group-page.strategy_successfully_updated"),
            this.translocoService.translate("sp-strategy-group-page.strategy_updated"),
          );
        },
        error: (err) => {
          this.toastrService.error(
            this.translocoService.translate("sp-strategy-group-page.error_updating_strategy") + err,
            this.translocoService.translate("sp-strategy-group-page.strategy_update_error"),
          );
        },
      });
  }

  private getTosroTooltip(supportedMarketplace: boolean, hasOneExactTargeting: boolean) {
    let res = "";
    if (this.isReadOnly()) {
      return this.translocoService.translate("sp-strategy-group-page.you_do_not_have_the_permission_to_activate_tosro");
    } else if (!supportedMarketplace) {
      return this.translocoService.translate("sp-strategy-group-page.tosro_not_supported_for_this_marketplace");
    } else {
      res = this.translocoService.translate("sp-strategy-group-page.activated_keywords_tracked");
    }
    if (!hasOneExactTargeting) {
      res = this.translocoService.translate("sp-strategy-group-page.tosro_only_for_kw");
    }
    return res;
  }

  private updateStrategyName(strategyId: number, newName: string) {
    return this.strategyService.updateStrategyName(
      this.accountMarketplace()!.accountId,
      this.accountMarketplace()!.marketplace,
      this.accountMarketplace()!.resourceOrganizationId!,
      strategyId,
      newName,
    );
  }

  private updateStrategyState(strategyId: number, newState: StrategyStateEnum) {
    this.strategyService
      .updateStrategyState(
        this.accountMarketplace()!.accountId,
        this.accountMarketplace()!.marketplace,
        this.accountMarketplace()!.resourceOrganizationId!,
        strategyId,
        newState,
      )
      .subscribe({
        next: () => {
          this.toastrService.success(
            this.translocoService.translate("sp-strategy-group-page.strategy_successfully_updated"),
            this.translocoService.translate("sp-strategy-group-page.strategy_updated"),
          );
        },
        error: (err) => {
          this.toastrService.error(
            this.translocoService.translate("sp-strategy-group-page.error_updating_strategy") + err,
            this.translocoService.translate("sp-strategy-group-page.strategy_update_error"),
          );
        },
      });
  }

  private updateStrategyOptimizer(strategy: StrategyEx, enable: boolean): void {
    const keywords: string[] = enable
      ? strategy.targetings.filter((t) => t.matchType == MatchType.exact).map((t) => t.targetingValue!)
      : strategy.topOfSearchRankings.map((t) => t.keyword!);

    this.spStrategiesService
      .updateTopOfSearchRankings(
        strategy,
        this.accountMarketplace()!.resourceOrganizationId!,
        keywords,
        enable ? UpdateStrategyTopOfSearchRankingsActionEnum.ADD : UpdateStrategyTopOfSearchRankingsActionEnum.DELETE,
      )
      .subscribe({
        next: () => {
          this.toastrService.success(
            this.translocoService.translate(
              `sp-strategy-group-page.top_of_search_rankings_optimizer_${enable ? "activated" : "deactivated"}`,
            ),
          );
        },
        error: (error) => {
          this.toastrService.error(
            `Error: ${error}`,
            this.translocoService.translate("sp-strategy-group-page.top_of_search_rankings_optimizer_update_error"),
          );
        },
      });
  }

  private openTargetingsModal(strategy: StrategyEx) {
    const targetingType =
      strategy.targetings.length > 0 && strategy.targetings[0].matchType === MatchType.asinSameAs
        ? StrategyTargetingType.PRODUCTS
        : StrategyTargetingType.KEYWORDS;
    const modalRef = this.modalService.openModal<TargetingsModalData, StrategyEx>(TargetingsModalComponent, {
      modalTitle: this.translocoService.translate("sd-strategy-creation.targetings"),
      data: {
        strategyType: strategy.strategyType,
        targetings: strategy.targetings,
        isReadOnly: this.isReadOnly(),
        strategy: strategy,
        strategyGroup: this.strategyGroup()!,
        targetingType: targetingType,
        accountMarketplace: this.accountMarketplace()!,
      },
    });

    modalRef.afterClosed().subscribe((updatedStrategy) => {
      if (updatedStrategy) {
        this.strategyGroup()!.brandStrategies = this.strategyGroup()!.brandStrategies.map((s) =>
          s.strategyId === updatedStrategy.strategyId ? updatedStrategy : s,
        );
        this.strategyGroup()!.keywordStrategies = this.strategyGroup()!.keywordStrategies.map((s) =>
          s.strategyId === updatedStrategy.strategyId ? updatedStrategy : s,
        );
      }
    });
  }

  private formatTargetings(targetings: Targeting[] | null | undefined): string {
    if (!targetings || targetings.length == 0) {
      return "No Targeting";
    }
    const { p, k } = targetings.reduce(
      ({ p, k }, t) => {
        if (t.matchType == MatchType.asinSameAs) {
          p++;
        } else {
          k++;
        }
        return { p, k };
      },
      { p: 0, k: 0 },
    );
    let res = "";
    if (p > 0) {
      res += `${p} Product${p > 1 ? "s" : ""}`;
    }
    if (k > 0) {
      res += p > 0 ? ", " : "";
      res += `${k} Keyword${k > 1 ? "s" : ""}`;
    }
    return res;
  }

  private openStrategyStats(strategy: StrategyEx) {
    this.modalService.openModal(StrategyStatsComponent, {
      modalTitle: strategy.name,
      data: {
        strategy,
        isReadOnly: this.isReadOnly(),
      },
    });
  }

  private openStrategyActivities(strategy: StrategyEx) {
    const modalOpts: ModalOptions<StrategyActivityModalData> = {
      data: {
        strategy,
      },
      modalTitle: this.translocoService.translate("sp-strategy-group-page.strategy_activities") + " - " + strategy.name,
    };
    this.modalService.openModal<StrategyActivityModalData, void>(StrategyActivityModalComponent, modalOpts);
  }

  private getActionButtons(strategy: StrategyEx): ActionButton[] {
    const buttons: ActionButton[] = [];
    buttons.push(this.ActionButtons["stats"](strategy));
    buttons.push(this.ActionButtons["activities"](strategy));
    buttons.push(this.ActionButtons["advanced"](strategy));

    if (strategy.strategyType !== StrategyType.PRODUCT) buttons.push(this.ActionButtons["delete"](strategy));
    else if (this.strategyGroup()!.productStrategies.length > 1)
      buttons.push(this.ActionButtons["deleteProductStrategy"](strategy));

    if (this.accountMarketplace()?.accessLevel == AccessLevel.ADMIN) {
      buttons.push(this.ActionButtons["copyStrategyId"](strategy));
    }
    return buttons;
  }

  private actionButtonColumn(): ColDef<StrategyEx> {
    return {
      ...actionColumnProperties<StrategyEx, string>(),
      cellRendererSelector: (params: ICellRendererParams<StrategyEx>) => {
        return {
          component: ActionButtonsComponent,
          params: {
            actionButtons: this.getActionButtons(params.data!),
          },
        };
      },
    };
  }

  openBlackListEditionModal() {
    const modalOption: ModalOptions<BlacklistModalData> = {
      modalTitle: this.translocoService.translate("sp-strategy-group-page.blacklist_update"),
      data: {
        isReadOnly: this.isReadOnly(),
        blacklist: this.strategyGroup()!.blacklist,
      },
    };

    const ref = this.modalService.openModal<BlacklistModalData, UpdateOperations<Targeting>>(
      BlacklistModalComponent,
      modalOption,
    );

    ref
      .afterClosed()!
      .pipe(
        switchMap((update: UpdateOperations<Targeting>) =>
          this.spStrategiesService.updateStrategyGroupBlacklist({
            strategyGroupId: this.strategyGroup()!.strategyGroupId!,
            accountId: this.accountMarketplace()!.accountId,
            marketplace: this.accountMarketplace()!.marketplace,
            toAdd: update.toAdd,
            toDelete: update.toDelete,
          }),
        ),
        take(1),
      )
      .subscribe({
        next: () => {
          this.toastrService.success(this.translocoService.translate("sp-strategy-group-page.blacklist_updated"));
        },
        error: (e: string) => {
          this.toastrService.error(
            e,
            this.translocoService.translate("sp-strategy-group-page.strategy_group_blacklist_update_error"),
          );
        },
      });
  }
}
